/**
 * TraderPicker.jsx — choose a trader to copy.
 *
 * ─── Two sources, Polymarket first ──────────────────────────────────────────
 * The previous version read /api/leaderboard/users — PolyEdge's own signups,
 * 39 accounts most of which are seedtest_* fixtures. Copy trading where the
 * only candidates are your own users is the product missing its front half.
 *
 * The Polymarket tab reads 2,449 analysed wallets instead.
 *
 * ─── Ranking is not copy_score ──────────────────────────────────────────────
 * analytics.db computes a copy_score per wallet, and it is unreliable: its
 * top-ranked wallets include one that lost $78K over 9 trades and one
 * averaging $400K per trade across 5. The endpoint therefore ranks by Sharpe
 * over wallets with 50+ trades and positive P&L.
 *
 * copy_score is still shown on each row — surfacing the disagreement is more
 * honest than hiding it, and it is a visible reminder that the score needs
 * fixing.
 */

import React, { useEffect, useMemo, useState } from "react";
import * as api from "../lib/api";
import { C, fUsd, pnlColor, shortAddr, Panel, Empty,
         Segment, SkeletonRow } from "../theme";

const SORTS = [
  { value: "sharpe", label: "Consistency" },
  { value: "pnl",    label: "P&L" },
  { value: "win",    label: "Win %" },
];

export default function TraderPicker({ onPick }) {
  const [source, setSource] = useState("polymarket");
  const [sort, setSort] = useState("sharpe");
  const [poly, setPoly] = useState(null);
  const [users, setUsers] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null);
    if (source === "polymarket") {
      setPoly(null);
      api.get("/api/polymarket/leaderboard", { sort, limit: 60 })
        .then((d) => setPoly(d))
        .catch((e) => setErr(e.message));
      api.get("/api/polymarket/leaderboard/stats")
        .then(setStats).catch(() => {});
    } else if (users === null) {
      api.get("/api/leaderboard/users", { mode: "all", limit: 100 })
        .then((d) => setUsers(Array.isArray(d) ? d : d?.users || []))
        .catch((e) => setErr(e.message));
    }
  }, [source, sort]);   // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (source === "polymarket") {
      const list = poly?.traders || [];
      return !s ? list : list.filter((r) => r.wallet.toLowerCase().includes(s));
    }
    const list = users || [];
    return !s ? list : list.filter((r) =>
      (r.username || "").toLowerCase().includes(s) ||
      (r.eoa_address || "").toLowerCase().includes(s));
  }, [source, poly, users, q]);

  const loading = source === "polymarket" ? poly === null : users === null;

  return (
    <div style={{ padding: 14 }}>

      <Segment
        value={source}
        onChange={setSource}
        style={{ marginBottom: 9 }}
        options={[
          { value: "polymarket", label: "Polymarket" },
          { value: "polyedge",   label: "PolyEdge" },
        ]}
      />

      {source === "polymarket" && (
        <Segment options={SORTS} value={sort} onChange={setSort}
                 style={{ marginBottom: 12 }} />
      )}

      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={source === "polymarket" ? "Wallet address" : "Username or address"}
        style={{ width: "100%", boxSizing: "border-box", padding: "11px 12px",
                 background: C.bg1, border: `1px solid ${C.line}`, color: C.ink,
                 fontFamily: C.mono, fontSize: 13, marginBottom: 12,
                 outline: "none" }}
      />

      {source === "polymarket" && (
        <div style={{ border: `1px solid ${C.line2}`, background: C.bg2,
                      padding: "10px 11px", marginBottom: 12 }}>
          <p style={{ fontSize: 10.5, color: C.sub, margin: 0, lineHeight: 1.7 }}>
            {stats
              ? `${stats.qualified} of ${stats.analysed.toLocaleString()} analysed wallets clear ${stats.min_trades}+ settled trades and positive P&L.`
              : "Ranked from Polymarket's own settled trades — not PolyEdge users."}
          </p>
        </div>
      )}

      {err && <Empty><span style={{ color: C.rose }}>{err}</span></Empty>}

      {loading && !err && (
        <>{[0, 1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}</>
      )}

      {!loading && rows.length === 0 && !err && (
        <Empty>No traders match that.</Empty>
      )}

      {!loading && source === "polymarket" && rows.map((r, i) => (
        <Panel key={r.wallet} className="pe-row" accent={pnlColor(r.pnl)}
               onClick={() => onPick({ address: r.wallet, username: null,
                                       total_pnl: r.pnl, win_rate: r.win_rate })}
               style={{ marginBottom: 7 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 9,
                        marginBottom: 5 }}>
            <span style={{ fontFamily: C.disp, fontSize: 17, color: C.dim,
                           minWidth: 18 }}>{i + 1}</span>
            <span style={{ flex: 1, fontSize: 12.5, color: C.ink,
                           overflow: "hidden", textOverflow: "ellipsis",
                           whiteSpace: "nowrap" }}>{r.short}</span>
            <span style={{ fontSize: 13.5, color: pnlColor(r.pnl),
                           flexShrink: 0 }}>{fUsd(r.pnl, true)}</span>
          </div>
          <div style={{ display: "flex", gap: 8, fontSize: 10.5, color: C.dim,
                        flexWrap: "wrap", paddingLeft: 27, marginBottom: 6 }}>
            <span>sharpe {r.sharpe}</span>
            <span>· {r.win_rate}% win</span>
            <span>· {r.trades.toLocaleString()} trades</span>
            <span>· {r.trading_days}d</span>
          </div>

          {/* The verdict that matters is not copy_score — it is whether the
              edge survives being copied at a fixed size. */}
          {r.warning && (
            <div style={{ marginLeft: 27, border: `1px solid ${C.rose}44`,
                          background: "rgba(244,114,182,0.05)",
                          padding: "7px 9px" }}>
              <p style={{ fontSize: 9.5, color: C.rose, margin: 0,
                          lineHeight: 1.6 }}>{r.warning}</p>
            </div>
          )}
        </Panel>
      ))}

      {!loading && source === "polyedge" && rows.map((r) => {
        const pnl = Number(r.total_pnl || 0);
        return (
          <Panel key={r.user_id || r.eoa_address} className="pe-row"
                 accent={pnlColor(pnl)} onClick={() => onPick(r)}
                 style={{ marginBottom: 7 }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "baseline", marginBottom: 5, gap: 8 }}>
              <span style={{ fontSize: 12.5, color: C.ink, overflow: "hidden",
                             textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.username || shortAddr(r.eoa_address)}
              </span>
              <span style={{ fontSize: 13.5, color: pnlColor(pnl),
                             flexShrink: 0 }}>{fUsd(pnl, true)}</span>
            </div>
            <div style={{ display: "flex", gap: 8, fontSize: 10.5, color: C.dim,
                          flexWrap: "wrap" }}>
              <span>{Number(r.win_rate || 0).toFixed(0)}% win</span>
              <span>· {r.total_trades || 0} trades</span>
              <span>· {shortAddr(r.eoa_address)}</span>
            </div>
          </Panel>
        );
      })}
    </div>
  );
}
